import React from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, Music2, Check } from 'lucide-react';
import { EXPERIENCE_MOMENTS, ESCAPE_PLAN, WHO_ITEMS, AFFIRMATION } from '../mock/mock';
import Reveal from '../components/Reveal';

const Experience = () => {
  return (
    <div className="bg-fire-ink">
      <Seo title="The ATF Experience" path="/experience" description="Bring the After the Fire™ Experience to your city, church, conference or retreat — where testimony meets music, ministry meets real conversation, and women rebuild together." />
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/brand/dr-gee-podium.jpg)', backgroundPosition: 'center 25%' }}
          data-testid="experience-hero-bg"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(23,17,13,0.82) 0%, rgba(23,17,13,0.72) 45%, rgba(23,17,13,0.92) 100%)' }} />
        <div className="absolute -top-32 right-0 w-[700px] h-[700px] rounded-full opacity-25 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(217,117,36,0.4), transparent 60%)' }} />
        <div className="max-w-[1000px] mx-auto px-6 text-center relative">
          <Flame className="w-9 h-9 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
          <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">THE AFTER THE FIRE™ EXPERIENCE</span>
          <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-4 leading-tight">This Isn’t Just an Event.<br />It’s an Experience.</h1>
          <p className="text-fire-cream/80 font-body max-w-2xl mx-auto mt-6 leading-relaxed">
            You can attend a conference, clap, cry, take a few pictures and go home exactly the same. That is not what I want for you. This is where testimony meets music, where ministry meets real conversation, and where women can take off the cape and simply be women.
          </p>
          <p className="font-cormorant italic text-fire-goldlight text-2xl mt-6">
            “Now that I survived — how do I LIVE?”
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <Link to="/book" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
              Bring ATF to Your City <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">
              Contact Dr. Gee
            </Link>
          </div>
        </div>
      </section>

      {/* What happens in the room */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">WHAT HAPPENS IN THE ROOM</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Signature Moments</h2>
            <p className="text-fire-cream/60 font-body mt-4">Every experience can be customized for the women in the room, but the journey centers around several signature moments.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {EXPERIENCE_MOMENTS.map((m, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="card-dark rounded-2xl p-7 h-full hover-lift">
                  {i === 4 ? <Music2 className="w-6 h-6 text-fire-gold mb-4" strokeWidth={1.5} /> : <Flame className="w-6 h-6 text-fire-gold mb-4" strokeWidth={1.5} />}
                  <h3 className="font-display text-xl text-fire-cream mb-3">{m.title}</h3>
                  <p className="text-fire-cream/60 font-body text-sm leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Fire Escape Plan */}
      <section className="py-24 ember-texture">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">A PRACTICAL TOOL</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">The Personal Fire Escape Plan™</h2>
            <p className="text-fire-cream/60 font-body mt-4">Because when a room is already filled with smoke, that’s a difficult time to start looking for the exit.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ESCAPE_PLAN.map((e, i) => (
              <Reveal key={i} delay={i * 50}>
                <div className="card-dark rounded-xl p-6 h-full hover-lift">
                  <span className="font-display text-3xl text-gradient-gold">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-marcellus text-fire-cream text-sm tracking-wide mt-3 mb-2 uppercase">{e.label}</h3>
                  <p className="text-fire-cream/55 font-body text-sm">{e.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who should experience */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">WHO SHOULD EXPERIENCE ATF</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Especially for the Woman Everybody Thinks Is Okay</h2>
            <p className="text-fire-cream/60 font-body mt-4">You don’t have to arrive broken. You don’t have to arrive healed. Just come as you are.</p>
          </Reveal>
          <div className="flex flex-wrap gap-3 justify-center">
            {WHO_ITEMS.map((w, i) => (
              <Reveal key={i} delay={i * 30}>
                <span className="inline-flex items-center gap-2 card-dark rounded-full px-4 py-2 text-fire-cream/75 font-body text-sm">
                  <Check className="w-3.5 h-3.5 text-fire-gold" /> {w}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Affirmation */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-fire-maroondark via-fire-ink to-fire-maroondark" />
        <div className="relative max-w-[800px] mx-auto px-6 text-center">
          <Reveal>
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">THE CLOSING AFFIRMATION</span>
            <div className="mt-8 space-y-4">
              {AFFIRMATION.map((line, i) => (
                <p key={i} className="font-cormorant text-2xl md:text-3xl text-fire-cream italic">{line}</p>
              ))}
            </div>
            <div className="gold-hairline max-w-xs mx-auto my-10" />
            <p className="font-marcellus tracking-[0.25em] text-fire-goldlight text-sm">TESTED BY THE FIRE. REFINED BY THE FLAME.™</p>
            <div className="flex flex-wrap gap-4 justify-center mt-9">
              <Link to="/book" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
                Book Dr. Gee <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">
                Shop ATF
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Experience;
